import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Lissajous / XY scope — wave vs delayed wave (pseudo stereo phase).
 * Looks like a living scribble when the track has body.
 */
export const lissajousViz: Visualizer = {
  id: 'lissajous',
  label: 'Lissajous XY',
  draw(frame) {
    const { ctx, w, h, wave, bands, t } = frame;
    clearBg(ctx, w, h, 0.22);

    const cx = w / 2;
    const cy = h / 2;
    const scale = Math.min(w, h) * (0.28 + bands.energy * 0.12);

    // delay in samples ~ phase offset reactive to mids
    const delay = Math.max(
      4,
      Math.floor(wave.length * (0.08 + bands.mids * 0.18 + 0.04 * Math.sin(t * 0.7))),
    );

    const vg = ctx.createRadialGradient(cx, cy, 4, cx, cy, Math.min(w, h) * 0.55);
    vg.addColorStop(0, 'rgba(10, 30, 36, 0.4)');
    vg.addColorStop(1, 'rgba(4, 8, 12, 0.95)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);

    // multi-pass phosphor trail
    const passes = [
      { a: 0.12, lw: 6, hue: 200 },
      { a: 0.28, lw: 3, hue: 175 },
      { a: 0.85, lw: 1.4, hue: 155 },
    ];

    for (const p of passes) {
      ctx.beginPath();
      const step = Math.max(1, Math.floor(wave.length / 700));
      let first = true;
      for (let i = 0; i < wave.length; i += step) {
        const xS = wave[i] || 0;
        const yS = wave[(i + delay) % wave.length] || 0;
        // mild rotation so it doesn't freeze axis-aligned
        const rot = t * (0.15 + bands.bass * 0.25);
        const xr = xS * Math.cos(rot) - yS * Math.sin(rot);
        const yr = xS * Math.sin(rot) + yS * Math.cos(rot);
        const x = cx + xr * scale * (12 + bands.kick * 4);
        const y = cy + yr * scale * (12 + bands.bass * 3);
        if (first) {
          ctx.moveTo(x, y);
          first = false;
        } else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `hsla(${p.hue + bands.highs * 40 + t * 8}, 95%, 60%, ${p.a + bands.energy * 0.1})`;
      ctx.lineWidth = p.lw + bands.beat * 0.8;
      ctx.lineJoin = 'round';
      ctx.shadowColor = `hsla(${p.hue}, 100%, 55%, 0.55)`;
      ctx.shadowBlur = 10 + bands.energy * 14;
      ctx.stroke();
    }
    ctx.shadowBlur = 0;

    // crosshair
    ctx.strokeStyle = 'rgba(86,240,200,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - scale, cy);
    ctx.lineTo(cx + scale, cy);
    ctx.moveTo(cx, cy - scale);
    ctx.lineTo(cx, cy + scale);
    ctx.stroke();

    drawHud(frame);
  },
};
